import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PhotoZoomModal } from '@/components/photos/PhotoZoomModal';
import { usePhotoZoom } from '@/hooks/usePhotoZoom';
import { PresenceIndicator } from '@/components/presence/PresenceIndicator';
import { MemberTypeBadgesLocal } from '@/components/admin/members/MemberTypeBadgesLocal';
import { TeamMemberBadge } from '@/components/ui/team-member-badge';
import { MapPin, Briefcase, MessageCircle, UserPlus, Linkedin } from 'lucide-react';
import { Member } from '@/types/member';
import { UserStatus } from '@/types/presence';
import { cn } from '@/lib/utils';

interface DirectoryMemberCardProps {
  member: Member;
  currentUserId?: string;
  onViewProfile: (member: Member) => void;
  onMessage: (member: Member) => void;
  onConnect: (member: Member) => void;
  userStatus?: UserStatus;
  allMembers: Member[];
  connectionStatus?: string | null;
}

export const DirectoryMemberCard: React.FC<DirectoryMemberCardProps> = ({
  member,
  currentUserId,
  onViewProfile,
  onMessage,
  onConnect,
  userStatus,
  allMembers,
  connectionStatus,
}) => {
  const { isZoomOpen, selectedMember, currentIndex, openZoom, closeZoom, navigateToNext, navigateToPrev } = usePhotoZoom(allMembers);
  
  const isCurrentUser = currentUserId && (member.user_id === currentUserId || member.id === currentUserId);
  const isConnected = connectionStatus === 'accepted';
  const isPending = connectionStatus === 'pending';
  const displayName = member.name || `${member.firstname || ''} ${member.lastname || ''}`.trim();
  const location = member.city && member.country 
    ? `${member.city}, ${member.country}` 
    : (member.country || member.city || '');

  return (
    <>
      <Card className={cn(
        "group overflow-hidden transition-all duration-300",
        "border-2 border-border/60 hover:border-primary/40",
        "shadow-sm hover:shadow-lg hover:shadow-primary/10",
        "bg-card hover:-translate-y-1"
      )}>
        {/* Photo Section */}
        <div 
          className="aspect-[4/3] overflow-hidden bg-muted cursor-pointer relative"
          onClick={() => openZoom(member, allMembers, allMembers?.findIndex(m => m.id === member.id))}
        >
          {member.image ? (
            <img 
              src={member.image} 
              alt={displayName} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center text-3xl font-bold text-primary">
                {displayName.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Presence indicator */}
          {userStatus && (
            <div className="absolute top-3 right-3">
              <PresenceIndicator status={userStatus} size="md" showLabel={false} />
            </div>
          )}

          {/* Team badge */}
          {member.is_team_member && (
            <div className="absolute top-3 left-3">
              <TeamMemberBadge size="sm" />
            </div>
          )}
        </div>

        <CardContent className="p-4 space-y-3">
          {/* Name & Type */}
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <h3 
                className="font-semibold text-base text-foreground line-clamp-1 cursor-pointer hover:text-primary transition-colors"
                onClick={() => onViewProfile(member)}
              >
                {displayName}
              </h3>
              <MemberTypeBadgesLocal types={member.member_types || []} primaryTypeOnly />
            </div>
            
            {/* Role & Company */}
            {(member.role || member.company) && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="line-clamp-1">
                  {member.role}{member.role && member.company ? ' — ' : ''}{member.company}
                </span>
              </div>
            )}
            
            {/* Location */}
            {location && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="line-clamp-1">{location}</span>
              </div>
            )}
          </div>

          {/* Skills */}
          {member.skills && member.skills.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {member.skills.slice(0, 3).map((skill, i) => (
                <Badge key={i} variant="secondary" className="text-xs px-2 py-0.5">
                  {skill}
                </Badge>
              ))}
              {member.skills.length > 3 && (
                <Badge variant="outline" className="text-xs px-2 py-0.5">
                  +{member.skills.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* LinkedIn */}
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#0A66C2] hover:text-[#004182] transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Linkedin className="h-3.5 w-3.5" />
              LinkedIn
            </a>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1 border-t border-border/50">
            {isCurrentUser ? (
              <div className="w-full text-center py-1.5 text-sm font-medium text-muted-foreground bg-muted rounded-md">
                Mon profil
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => onMessage(member)}
                >
                  <MessageCircle className="h-3.5 w-3.5 mr-1" />
                  Message
                </Button>
                <Button
                  variant={isConnected ? "secondary" : isPending ? "outline" : "default"}
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => onConnect(member)}
                  disabled={isConnected || isPending}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  {isConnected ? 'Connecté' : isPending ? 'En attente' : 'Connecter'}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Photo Zoom Modal */}
      {selectedMember && (
        <PhotoZoomModal
          isOpen={isZoomOpen}
          onClose={closeZoom}
          member={selectedMember}
          allMembers={allMembers}
          currentIndex={currentIndex}
          onNavigate={(direction) => direction === 'next' ? navigateToNext() : navigateToPrev()}
          onMessage={onMessage}
          onConnect={onConnect}
        />
      )}
    </>
  );
};
